import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAccountContext } from '../../../contexts/AccountContext'
import { useAuth } from '../../../hooks/useAuth'
import { useMissingReadingsDay, useMissingReadingsSummary } from '../../../hooks/admin/useMissingReadings'
import DayStepper, { visibleWindow } from '../../../components/admin/missingReadings/DayStepper'
import {
  LabeledSwitch,
  MISSING_READINGS_FLEET_MAX_DAYS,
  MISSING_READINGS_MAX_DAYS,
  ReconfiguredMark,
} from '../../../components/admin/missingReadings/controls'
import PeriodSummary from '../../../components/admin/missingReadings/PeriodSummary'
import type { DayDeviceBreakdown } from '../../../types/missingReadings'
import { useUrlAccount, useUrlBoolean, useUrlNumber, useUrlDayRange } from '../../../hooks/useUrlState'

/**
 * One day, fleet-wide: which devices missed messages, and how many.
 *
 * This is the middle rung of the drill-in. The dashboard card says a day was
 * bad; this says who made it bad; the per-device page below says which
 * individual transmissions never arrived.
 *
 * "Missed" here is the exact signal — gaps in each device's Sigfox sequence
 * number across every message type it sends. The expected/missing columns are
 * the interval estimate and are withheld (—, marked *) for a device that was
 * reconfigured that day.
 */

export default function MissingReadingsDayPage() {
  const { date } = useParams<{ date: string }>()
  const navigate = useNavigate()

  // ?accountId= arrives from the dashboard card when an account was selected
  // there. Undefined means every account; 'none' is the no-account bucket.
  const [accountId, setAccountId] = useUrlAccount('accountId')
  const accountQuery = accountId === undefined ? '' : `?accountId=${accountId === null ? 'none' : accountId}`
  const { isAdmin } = useAuth()
  const admin = isAdmin()

  const day = date ?? ''

  /**
   * Follow the device selector in the left nav, the same way the device day
   * page does: picking a device here opens that device's sequence detail for
   * this same day, rather than leaving the sidebar highlighting a device the
   * page never shows.
   *
   * `replace`, not push. The ?sigfoxId= the sidebar appended is a transient
   * state of this URL; pushing past it would leave it in history, and pressing
   * Back would land on it and immediately bounce forward again.
   */
  const [searchParams] = useSearchParams()
  const { devices: accountDevices } = useAccountContext()
  const selectedSigfoxId = searchParams.get('sigfoxId')
  useEffect(() => {
    if (!selectedSigfoxId || !day) return
    const selected = (accountDevices as Record<string, { deviceId: number }>)[selectedSigfoxId]
    if (!selected) return
    navigate(`/missing-readings/${day}/${selected.deviceId}?${searchParams.toString()}`, { replace: true })
  }, [selectedSigfoxId, accountDevices, day, navigate, searchParams])
  const { data, isLoading, isError, error } = useMissingReadingsDay(day || null, admin, accountId)
  // Only for the stepper's markers — the full window is already cached by the
  // dashboard card in most arrivals here, so this is usually free.
  const [rangeDays, setRangeDays] = useUrlNumber('range', 90)
  const [zoom, setZoom] = useUrlDayRange('zoom')
  // Transient, so deliberately not in the URL: it only lives while a stat in
  // the period summary is being hovered.
  const [highlightDays, setHighlightDays] = useState<Set<string> | null>(null)
  // Scoped to the same account as the table below. Unscoped, the chart would
  // show fleet-wide bars above an account-filtered table — two different
  // questions stacked on one screen.
  const { data: summary } = useMissingReadingsSummary(rangeDays, admin, accountId)
  // Unscoped, only to name the account and offer the way back out.
  const { data: allAccounts } = useMissingReadingsSummary(rangeDays, admin)
  const accountName =
    accountId === undefined
      ? null
      : allAccounts?.accounts.find((a) => a.accountId === accountId)?.accountName ??
        (accountId === null ? 'No account' : `Account ${accountId}`)

  // The fleet's days in the shape the shared bars take. A fleet day is never
  // "reconfigured" as a whole; its expected total already leaves those out.
  const stripRows = (summary?.days ?? []).map((d) => ({
    day: d.day,
    missedMessages: d.missedMessages,
    receivedReadings: d.receivedReadings,
    expectedReadings: d.expectedReadings,
    missingReadings: d.missingReadings,
    reconfigured: false,
  }))

  const goToDay = (next: string) => navigate(`/missing-readings/${next}${accountQuery}`)

  // Grouped by default: 17 bare Sigfox ids say nothing about who is affected,
  // and "one account is having a bad day" is a different problem from "the
  // whole fleet is".
  const [groupByAccount, setGroupByAccount] = useUrlBoolean('grouped', true)

  /** Devices bucketed under their account, worst account first. */
  const accountGroups = useMemo(() => {
    const groups = new Map<
      string,
      {
        accountId: number | null
        accountName: string | null
        devices: DayDeviceBreakdown[]
        missedMessages: number
      }
    >()
    for (const dev of data?.devices ?? []) {
      const key = dev.accountId === null ? 'none' : String(dev.accountId)
      const group = groups.get(key) ?? {
        accountId: dev.accountId,
        accountName: dev.accountName,
        devices: [],
        missedMessages: 0,
      }
      group.devices.push(dev)
      group.missedMessages += dev.missedMessages
      groups.set(key, group)
    }
    return Array.from(groups.entries())
      .map(([key, g]) => ({ key, ...g }))
      .sort((a, b) => b.missedMessages - a.missedMessages)
  }, [data])

  /** One device row, indented when it sits under an account heading. */
  const deviceRow = (dev: DayDeviceBreakdown, indented: boolean) => (
    <tr key={dev.deviceId} className="hover:bg-gray-50">
      <td className={`px-4 py-2 ${indented ? 'pl-8' : ''}`}>
        <Link to={`/missing-readings/${day}/${dev.deviceId}`} className="text-blue-600 hover:underline">
          {dev.sigfoxId}
        </Link>
        {/* Only worth naming per row when the account headings are not
            already doing it. */}
        {!indented && dev.accountName && <span className="ml-2 text-xs text-gray-400">{dev.accountName}</span>}
        {dev.reconfigured && <ReconfiguredMark title="Reconfigured this day — expected count not estimated" />}
      </td>
      <td
        className={`px-4 py-2 text-right tabular-nums ${
          dev.toleranceExceeded ? 'text-red-600 font-semibold' : 'text-red-600'
        }`}
        title={dev.toleranceExceeded ? 'Over the configured tolerance' : undefined}
      >
        {dev.missedMessages.toLocaleString()}
      </td>
      <td className="px-4 py-2 text-right tabular-nums text-gray-600">{dev.receivedReadings.toLocaleString()}</td>
      <td className="px-4 py-2 text-right tabular-nums text-gray-400">{dev.expectedReadings ?? '—'}</td>
    </tr>
  )

  const header = (
    <div className="mb-4">
      <Link to="/dashboard" className="text-sm text-blue-600 hover:underline">
        ‹ Back to dashboard
      </Link>
      <h1 className="text-2xl font-bold text-gray-900 mt-2">
        Missing readings — {day}
        {accountName && <span className="text-gray-400 font-normal"> · {accountName}</span>}
      </h1>
      <p className="text-sm text-gray-600 mt-1">
        {accountName ? (
          <>
            Devices in {accountName} that missed at least one Sigfox message on this day (UTC).{' '}
            <Link to={`/missing-readings/${day}`} className="text-blue-600 hover:underline">
              Show all accounts
            </Link>
          </>
        ) : (
          'Devices that missed at least one Sigfox message on this day (UTC).'
        )}
      </p>
    </div>
  )

  if (isError) {
    const code = (error as any)?.response?.data?.error?.code
    return (
      <div className="p-6 max-w-3xl mx-auto">
        {header}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-10 text-center">
          <p className="text-gray-900 font-medium">
            {code === 'INVALID_DATE' ? 'That day is outside the window' : 'Could not load this day'}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            {code === 'INVALID_DATE'
              ? `Missing-readings history goes back ${MISSING_READINGS_MAX_DAYS} days and does not include the future.`
              : (error as any)?.response?.data?.error?.message || 'Please try again.'}
          </p>
        </div>
      </div>
    )
  }

  const devices = data?.devices ?? []

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {header}

      <div className="mb-4">
        <DayStepper
          date={day}
          onChange={goToDay}
          rows={stripRows}
          rangeDays={rangeDays}
          onRangeChange={setRangeDays}
          zoom={zoom}
          onZoomChange={setZoom}
          highlightDays={highlightDays}
          maxRangeDays={MISSING_READINGS_FLEET_MAX_DAYS}
        />
        {summary && (
          <div className="mt-2">
            <PeriodSummary
              rows={summary.days}
              {...visibleWindow(rangeDays, MISSING_READINGS_FLEET_MAX_DAYS, zoom)}
              zoomed={!!zoom}
              onSelectDay={goToDay}
              onHighlight={(days) => setHighlightDays(days ? new Set(days) : null)}
            />
          </div>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : devices.length === 0 ? (
        // Phrased as the finding it is. An empty table on a diagnostic page
        // reads as broken unless it says plainly that nothing was wrong.
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-10 text-center">
          <p className="text-gray-900 font-medium">
            No device missed a message on {day}
            {accountName ? ` in ${accountName}` : ''}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Every transmission {accountName ?? 'the fleet'} sent that day arrived in sequence.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <p className="text-sm text-gray-600">
              {data!.totals.missedMessages.toLocaleString()} missed message
              {data!.totals.missedMessages === 1 ? '' : 's'} across {data!.totals.devicesAffected} device
              {data!.totals.devicesAffected === 1 ? '' : 's'}
              {accountId === undefined && groupByAccount && accountGroups.length > 0 && (
                <span className="text-gray-400">
                  {' '}
                  in {accountGroups.length} account{accountGroups.length === 1 ? '' : 's'}
                </span>
              )}
            </p>
            {/* Only one account is on screen when the page is scoped, so
                grouping by account would draw a single heading over the whole
                table. */}
            {accountId === undefined && (
              <LabeledSwitch checked={groupByAccount} onCheckedChange={setGroupByAccount} label="Group by account" />
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-gray-600">
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase">Device</th>
                  <th className="px-4 py-2 text-right text-xs font-medium uppercase">Missed</th>
                  <th className="px-4 py-2 text-right text-xs font-medium uppercase">Received</th>
                  <th className="px-4 py-2 text-right text-xs font-medium uppercase">Expected</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {groupByAccount && accountId === undefined
                  ? accountGroups.flatMap((group) => [
                      // Clicking a heading scopes the whole page — chart,
                      // totals and table — to that account, the same way the
                      // dashboard card's account column does.
                      <tr
                        key={`acct-${group.key}`}
                        onClick={() => setAccountId(group.accountId)}
                        title={`Show only ${group.accountName ?? 'devices with no account'}`}
                        className="bg-gray-50/70 cursor-pointer hover:bg-gray-100"
                      >
                        <td className="px-4 py-1.5 text-xs font-semibold text-blue-700 hover:underline">
                          {group.accountName ?? 'No account'}
                          <span className="ml-1.5 font-normal text-gray-400">
                            {group.devices.length} device{group.devices.length === 1 ? '' : 's'}
                          </span>
                        </td>
                        <td className="px-4 py-1.5 text-right text-xs font-semibold tabular-nums text-gray-700">
                          {group.missedMessages.toLocaleString()}
                        </td>
                        <td colSpan={2} />
                      </tr>,
                      ...group.devices.map((dev) => deviceRow(dev, true)),
                    ])
                  : devices.map((dev) => deviceRow(dev, false))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-gray-400 mt-2">
            Missed is exact — gaps in each device's Sigfox sequence number, across every message type it sends. Received
            and Expected count scheduled readings only, and Expected is an estimate from the device's configured
            interval.
          </p>
        </>
      )}
    </div>
  )
}
